import React, { useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import clsx from "clsx";

import { IconGoLink } from "../../../../components/SectionGoButtons";
import {
  EMPTY_CELL,
  TASK_REPORT_STATUS,
  TASK_REPORT_STATUS_OPTIONS,
  getAddressHref,
  getComplexHref,
  getTaskReportDailyLocation,
} from "../../helpers";

const statusToExecutionLabel = (status, dailyCount) => {
  if (status === TASK_REPORT_STATUS.CONFLICT) return "Несколько выставлений на локации";
  const option = TASK_REPORT_STATUS_OPTIONS.find((o) => o.value === status);
  if (!option?.label) return "";
  // Для остальных статусов показываем исходные формулировки.
  return option.label;
};

const TaskDrawer = ({ open, item, onClose }) => {
  const classes = useStyles();
  if (!open || !item) return null;

  const executionPillClassName = clsx(
    classes.executionPill,
    item?.status === TASK_REPORT_STATUS.MATCHED && classes.executionPillMatched,
    item?.status === TASK_REPORT_STATUS.TASK_ONLY && classes.executionPillTaskOnly,
    item?.status === TASK_REPORT_STATUS.DAILY_ONLY && classes.executionPillDailyOnly,
    item?.status === TASK_REPORT_STATUS.CONFLICT && classes.executionPillConflict,
  );

  const roadName = item?.task_data?.road_name || EMPTY_CELL;
  const fixationPoint = item?.task_data?.fixation_point || EMPTY_CELL;
  const note = item?.task_data?.note || EMPTY_CELL;

  const dailyRowsRaw = Array.isArray(item?.daily_data) ? item.daily_data : [];

  const dailyCount = dailyRowsRaw.length;
  const executionLabel = statusToExecutionLabel(item?.status, dailyCount);

  const rows = dailyRowsRaw.map((d) => ({
    location: getTaskReportDailyLocation(d) || { id: "", name: "" },
    start_time: d?.start_time || EMPTY_CELL,
    end_time: d?.end_time || EMPTY_CELL,
    complex_serial: d?.complex_serial || EMPTY_CELL,
    complex_id: d?.complex_id || "",
    grz_ts: d?.grz_ts || EMPTY_CELL,
  }));

  const showDeploymentsTable = rows.length > 1;
  const operatorGrz = !showDeploymentsTable
    ? (rows[0]?.grz_ts || EMPTY_CELL)
    : EMPTY_CELL;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ className: classes.paper }}>
      <div className={classes.header}>
        <Typography className={classes.title}>Информация о выставлении</Typography>
        <IconButton onClick={onClose} size="small" aria-label="Закрыть">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={classes.body}>
        <div className={classes.row}>
          <span className={classes.label}>Наименование дороги</span>
          <span className={classes.value}>{roadName || "Н/Д"}</span>
        </div>
        <div className={classes.row}>
          <span className={classes.label}>Точка фиксации</span>
          <span className={classes.value}>{fixationPoint || "Н/Д"}</span>
        </div>
        <div className={classes.row}>
          <span className={classes.label}>Примечание</span>
          <span className={classes.value}>{note || "Н/Д"}</span>
        </div>
        <div className={classes.row}>
          <span className={classes.label}>Выполнение задания</span>
          <span className={executionPillClassName}>{executionLabel || "Н/Д"}</span>
        </div>
        {!showDeploymentsTable && (
          <div className={classes.row}>
            <span className={classes.label}>ГРЗ оператора</span>
            <span className={classes.value}>{operatorGrz || "Н/Д"}</span>
          </div>
        )}

        {showDeploymentsTable && (
          <>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>Выставления на локации</Typography>
              <span className={classes.badge}>{Number(dailyCount || 0)}</span>
            </Box>
            <Box className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Местоположение</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", pt: 0, pb: 0, lineHeight: '1.1!important' }}>Время<br/>начала</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", pt: 0, pb: 0, lineHeight: '1.1!important' }}>Время<br/>окончания</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>Номер комплекса</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", pt: 0, pb: 0, lineHeight: '1.1!important' }}>ГРЗ<br/>оператора</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, idx) => (
                    <DeploymentRow
                      key={`${r.location?.id || r.location?.name || "row"}-${idx}`}
                      row={r}
                      classes={classes}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </div>
    </Drawer>
  );
};

const CellWithGoLink = ({ href, children, classes, visible = false, GoLink = IconGoLink }) => {
  if (!children || children === EMPTY_CELL) {
    return <span className={classes.cellText}>{EMPTY_CELL}</span>;
  }

  return (
    <Box className={classes.cellLinkWrap}>
      <span className={classes.cellText}>{children}</span>
      {href ? <GoLink href={href} visible={visible} /> : null}
    </Box>
  );
};

const DeploymentRow = ({ row, classes }) => {
  const [isHovered, setIsHovered] = useState(false);
  const addressHref = getAddressHref(row.location);
  const complexHref = getComplexHref({
    complex_id: row.complex_id,
    complex_serial: row.complex_serial,
  });
  const locationName = row.location?.name || EMPTY_CELL;

  return (
    <TableRow
      hover
      className={classes.tableRow}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableCell>
        <CellWithGoLink href={addressHref} classes={classes} visible={isHovered}>
          {locationName}
        </CellWithGoLink>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{row.start_time || EMPTY_CELL}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{row.end_time || EMPTY_CELL}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <CellWithGoLink href={complexHref} classes={classes} visible={isHovered}>
          {row.complex_serial || EMPTY_CELL}
        </CellWithGoLink>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{row.grz_ts || EMPTY_CELL}</TableCell>
    </TableRow>
  );
};

const useStyles = makeStyles(() => ({
  paper: {
    maxWidth: "100%",
    borderLeft: "1px solid #E4E4E7",
    zIndex: "1400 !important",
    boxShadow: "-8px 0 24px -4px rgba(0, 0, 0, 0.08)",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    "@media (min-width: 600px)": {
      width: 760,
      top: 12,
      height: "calc(100% - 24px)",
      borderRadius: 10,
      right: 20,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 20px",
    flexShrink: 0,
    backgroundColor: "#FFFFFF",
  },
  title: {
    fontWeight: 500,
    fontSize: 20,
    lineHeight: "28px",
    color: "#111111",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    padding: "16px 20px 20px",
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    padding: "8px 0",
  },
  label: {
    width: 180,
    flexShrink: 0,
    fontSize: 14,
    fontWeight: 500,
    color: "#000000",
    lineHeight: "20px",
  },
  value: {
    flex: 1,
    fontSize: 14,
    color: "#000000",
    lineHeight: "20px",
    minWidth: 0,
    wordBreak: "break-word",
  },
  executionPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 12px",
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.1,
  },
  // Цвета “chip” должны совпадать с `Table` (statusPill*)
  executionPillMatched: {
    backgroundColor: "#DCFCE7",
    color: "#116932",
  },
  executionPillTaskOnly: {
    backgroundColor: "#FEE2E2",
    color: "#991919",
  },
  executionPillDailyOnly: {
    backgroundColor: "#FEF9C3",
    color: "#713F12",
  },
  executionPillConflict: {
    backgroundColor: "#DBEAFE",
    color: "#173DA6",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 24,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    color: "#000000",
    fontWeight: 500,
    lineHeight: "24px",
  },
  badge: {
    fontSize: 12,
    fontWeight: 500,
    color: "#432DD7",
    backgroundColor: "#E0E7FF",
    borderRadius: 16,
    padding: "2px 10px",
  },
  tableWrap: {
    // Intentionally scoped via `tableWrap` className.
    // Typography for table cells in the drawer table.
    "& .MuiTableCell-root": {
      fontSize: 14,
      lineHeight: "20px",
      fontWeight: 500,
      color: "#000000 !important",
    },
    "& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root": {
      fontSize: 14,
      lineHeight: "20px",
      fontWeight: 500,
      color: "#000000 !important",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#EEF2FF",
    },
  },
  cellText: {
    fontSize: 14,
    lineHeight: "20px",
    color: "#18181B",
    wordBreak: "break-word",
  },
  cellLinkWrap: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
}));

export default TaskDrawer;

